import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import OSAnomalyRulesService, { fields } from '../../../../data/os-anomaly-rules-service';
import Auth, { ParamsSerializer, ProfileContext } from '../../../../data/auth-service';

import useWindowSize from '../../../../libraries/usewindowsize';
import { Flatten, RuleTitle } from '../../../../libraries/helpers';

import SearchBar from '../../../../components/searchbar';
import { LoadingOverlay, NoRowsOverlay } from '../../../../components/xgrid/overlays';
import HighlightText from '../../../../components/highlightText';
import CellExpand from '../../../../components/xgrid/cell-expand';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    // marginBottom: 24,
    },
}));

const EMPTY_FILTER = {
    filters: {
        grade: {
            enabled: false,
            operator: '=',
            value: 0,
        },
        confidence: {
            enabled: false,
            operator: '=',
            value: 0,
        },
    },
    operator: 'and',
};

const MAX_SEARCH_LENGTH = 100;

const OpenSearchAnomalies = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);

    const [rules, setRules] = useState([]);
    const [ogRules, setOGRules] = useState([]);
    const [filters, setFilters] = useState({});
    const [sData, setSData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState();
    const [columns, setColumns] = useState(fields);
    const [columnWidth, setColumnWidth] = useState({});
    const [columnsOrder, setColumnsOrder] = useState({});
    const [changeList, setChangeList] = useState({});

    const perms = Auth.permissions.get('OpenSearchAnomalies');

    const handleSearchChange = ({ search }) => setSearch(search);

    useEffect(() => {
        getFilters(getRules);
    }, []);

    useEffect(() => {
        const profile = getProfile('os_anomaly_rules');
        setColumnWidth(profile.widths || {});

        // this is the only time we set this variable to avoid flickering
        setColumnsOrder(profile.columns || fields.map((f, i) => ({ [f.variable]: i })));
    }, [getProfile]);

    // columns order updater
    useEffect(() => {
        // subscribe to columnHeaderDragEnd which is much better than onColumnOrderChange
        return tableAPIRef.current.subscribeEvent(
            'columnHeaderDragEnd',
            params => {
                const columns = tableAPIRef.current.getAllColumns().reduce((a, c, i) => {
                    return { ...a, [c.variable]: i };
                }, []);

                updateProfile('os_anomaly_rules', {
                    ...getProfile('os_anomaly_rules'),
                    columns,
                });

                setColumnsOrder(columns);
            }
        );
    }, [getProfile, tableAPIRef, updateProfile]);

    // columns updator
    useEffect(() => {
        setColumns(fields.map(f => ({
            ...f,
            width: columnWidth[f.variable] || f.width,
            renderCell: tableRenderers[f.variable] || textRenderer(f),
            renderHeader: headerRenderers[f.variable],
        })).sort((a, b) => (columnsOrder[a.variable] || 0) - (columnsOrder[b.variable] || 0)));
    }, [columnWidth, columnsOrder, search, rules]);

    useEffect(() => {
        const searchFn = v => !search || v.toString().toLowerCase().includes(search.toLowerCase());
        setSData((rules || [])
            .filter(object => Object.values(Flatten({ object }))
            .some(searchFn)));
    }, [search, rules]);

    // fetch and filter on search
    const getRules = (filterCheck) => {
        setLoading(true);
        OSAnomalyRulesService.listRules().then(res => {
            // setRules(_cloneDeep(_get(res, 'Detectors', [])));
            setOGRules(_cloneDeep(_get(res, 'Detectors', [])));
            if (res && _has(res, 'Detectors')) {
                setRules(res.Detectors);
                const filterPromises = [];
                res.Detectors.forEach(x => {
                    if (!_has(filterCheck, x.uuid)) {
                        filterPromises.push(OSAnomalyRulesService.updateRuleFilters(x.name, x.uuid, EMPTY_FILTER));
                    }
                });

                if (filterPromises.length) {
                    Promise.all(filterPromises).then(() => {
                        return;
                    }, err => {
                        toast.error('Error enabling default filters for detection rule(s)');
                        console.error('Error enabling default filters for detection rule(s) ::: ', err);
                    });
                }
            }
            setLoading(false);
        }, err => {
            toast.error('Unable to get detector rules: ' + _get(err, 'error.reason', ''));
            console.error(err);
            setLoading(false);
        });
    };

    const getFilters = (ruleCallback) => {
        OSAnomalyRulesService.listFilters().then(res => {
            setFilters(res);
            ruleCallback(res);
        }, err => {
            console.error(err);
        });
    };

    const hasFilter = (id) => {
        const rf = _get(filters, `${id}`, {});
        if (_has(rf, 'filters')) {
            return Object.values(rf.filters).some(f => f.enabled);
        }
        return false;
    };

    const handleEnable = (enabled, row) => {
        setRules(current => {
            return current.map(d => d.uuid === row.uuid ? (enabled ? { ...d, status: 'ENABLED' } : { ...d, status: 'DISABLED'}) : d);
        });
        const cl = _cloneDeep(changeList);
        if (_has(cl, row.uuid)) {
            if (enabled && !cl[row.uuid]) {
                delete cl[row.uuid];
            } else if (!enabled && cl[row.uuid]) {
                delete cl[row.uuid];
            }
        } else {
            cl[row.uuid] = enabled;
        }
        setChangeList(cl);
        // OSAnomalyRulesService.enableAnomalyRule(row.uuid, enabled);
    };

    // TODO: need API to enable/disable all rules
    const handleEnableAll = (enabled) => {
        const cl = _cloneDeep(changeList);
        let r = _cloneDeep(rules);
        r = r.map(r => {
            if (_has(cl, r.uuid)) {
                if (enabled && !cl[r.uuid]) {
                    delete cl[r.uuid];
                } else if (!enabled && cl[r.uuid]) {
                    delete cl[r.uuid];
                }
            } else if ((enabled && r.status === 'DISABLED') || (!enabled && r.status !== 'DISABLED')) {
                cl[r.uuid] = enabled;
            }
            return {
                ...r,
                status: enabled ? 'ENABLED' : 'DISABLED'
            };
        });
        setChangeList(cl);
        setRules(r);
    };

    const handleColumnResize = params => {
        const profile = getProfile('osanomalies');
        updateProfile('osanomalies', {
            ...profile,
            widths: { ...profile.widths || {}, [params.colDef.variable]: params.width },
        });

        setColumnWidth(current => ({
            ...current,
            [params.colDef.variable]: params.width,
        }));
    };

    const handleEdit = (row) => {
        const name = _get(row, 'name');
        const id = row.uuid;
        navigate(`/system/anomaly_detection/opensearch/${name}?${ParamsSerializer({ id })}`);
    };

    const tableRenderers = {
        name: d => {
            return <span>{RuleTitle(d.row.name)}</span>
        },
        description: d => {
            return <CellExpand { ...{ ...d, value: d.row.description } } />;
        },
        filters: d => {
            const id = d.row.uuid;
            return hasFilter(id)
                ? <CheckCircleIcon fontSize='small' color = 'primary' />
                : <RadioButtonUncheckedIcon fontSize='small' color = 'primary' />;
        },
        enabled: d => (
            <Checkbox
                style={{ justifyContent: 'center' }}
                size="small"
                disabled={!perms.write}
                checked={d.row.status === 'DISABLED' ? false : true}
                onClick={(e) => {
                    e.stopPropagation();
                    handleEnable(e.target.checked, d.row);
                }}
            />
        ),
        edit: d => (
            perms.write ? <Box>
                <IconButton color="secondary" onClick={() => { handleEdit(d.row) }}><EditIcon /></IconButton>
            </Box> : null),
    };

    const textRenderer = f => d => (<HighlightText text={`${f.valueGetter(d) === '????' ? '' : f.valueGetter(d)}`} search={search} />);

    const headerRenderers = {
        enabled: perms.delete ? c => {
            const all = rules.filter(d => d.status !== 'DISABLED').length === rules.length;
            const none = rules.filter(d => d.status === 'DISABLED').length === rules.length;
            return <FormControlLabel
                label="Activate All"
                control={
                    <Checkbox
                        size="small"
                        disabled={!perms.write}
                        checked={all || false}
                        indeterminate={!all && !none}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEnableAll(e.target.checked);
                        }}
                    />
                }
            />;
        } : null
    };

    const handleSave = () => {
        const createPromises = [];
        const enablePromises = [];
        Object.keys(changeList).forEach((x) => {
            enablePromises.push(OSAnomalyRulesService.enableAnomalyRule(x, changeList[x]));
        });
        
        Promise.all(enablePromises).then(res => {
            getRules(filters);
            toast.success('Successfully updated detectors');
        }, err => {
            toast.error('Unable to update detectors');
            console.error(err);
        });
    };

    const handleRevert = () => {
        setChangeList({});
        setRules(ogRules);
    };

    return (
        <Grid container spacing={2}>
            <Grid container item xs={12}>
                <Grid item xs={10}>
                    <h3>OpenSearch Rules</h3>
                </Grid>
                {perms.write && <Grid container item xs={2} justifyContent="flex-end" alignContent="center">
                    <Box mr={2}>
                        <Button disabled={!Object.keys(changeList).length} variant="contained" color="primary" onClick={handleSave}>Save</Button>
                    </Box>
                    <Box>
                        <Button disabled={!Object.keys(changeList).length} variant="contained" color="secondary" onClick={handleRevert}>Revert</Button>
                    </Box>
                </Grid>}
            </Grid>
            <Grid item xs={12}>
                <Grid container direction="row" className={classes.fullWidth} mb={3} display="flex" justify="space-between">
                    <Grid xs={12} item>
                        <SearchBar immediate={false}
                            maxLength={MAX_SEARCH_LENGTH}
                            style={{ width: '100%' }}
                            onSearch={handleSearchChange}
                            searchservice={OSAnomalyRulesService} />
                    </Grid>
                </Grid>
                <Box style={{ height: (height || 500) - 300 }}>
                    <DataGridPro
                        apiRef={tableAPIRef}
                        components={{
                            LoadingOverlay,
                            NoRowsOverlay,
                        }}
                        loading={loading}
                        rows={sData.length ? sData : rules}
                        columns={columns}
                        disableMultipleSelection
                        disableColumnFilter
                        disableColumnMenu
                        disableVirtualization
                        onRowClick={() => {}}
                        onColumnResize={handleColumnResize}
                        getRowId={row => row.name}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};

export default OpenSearchAnomalies;
